import React, { useState, useEffect } from "react";
import "../styles/ProductDetailExtra.css";
import QnaModal from "./QnaModal";
import {useNavigate} from "react-router-dom";
import {useLoggedInUserId} from "../../api/GetLoggedInUserId";
import LoginRequiredModal from "./LoginRequiredModal";

/**
 * ProductDetailExtra 컴포넌트
 *
 * Props:
 *  - item: 서버에서 받아온 Item 객체
 *      - item.attachFiles: 첨부파일 배열 (각 객체는 filePath라는 이미지 URL 필드를 가진다고 가정)
 *      - item.videoUrl: 상품 관련 동영상 URL (YouTube)
 */
const ProductDetailExtra = ({ item, reviews, qa }) => {
    var navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("detail");
    const [image, setImages] = useState([]);
    const [reviewList, setReviews] = useState([]);
    const [averageRating, setAverageRating] = useState(0);
    const [qaList, setQaList] = useState([]);
    const [isQnaModalOpen, setIsQnaModalOpen] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false); // ✅ 로그인 모달 상태 추가
    const [selectedVideo, setSelectedVideo] = useState(null); // 선택된 동영상 상태 추가
    const [videoUrl, setVideoUrl] = useState(null); // 동영상 URL 상태 추가
    const { data: memberId } = useLoggedInUserId(); // ✅ 로그인된 사용자 ID 가져오기


    useEffect(() => {
        if (item && item.detailImgPath) {
            // 첨부파일 목록에서 상세페이지에 사용할 이미지 URL들을 추출합니다.
            // 예: 각 AttachFile 객체에 filePath 필드가 있다고 가정합니다.
            const detailImages = item.detailImgPath;
            setImages(detailImages);
        } else {
            setImages([]);
        }

        // 비디오 URL 설정 불러오기
        if (item && item.videoUrl) {
            setVideoUrl(item.videoUrl);
        }

        // 리뷰가 존재할 경우
        if (reviews){
            var reviewList = reviews;
            setReviews(reviewList);

            ReviewSection({ reviews: reviewList, totalReviews: reviewList.length, tags: [] });
        } else {
            setReviews([]);
        }

        // qa가 존재할 경우
        if (qa) {
            setQaList(qa);
        } else {
            setQaList([]);
        }


    }, [item], [reviews], [qa]);

    const calculateAverageRating = (reviews) => {
        if (reviews.length === 0) return 0;
        const total = reviews.reduce((acc, review) => acc + Number(review.rate), 0);
        return (total / reviews.length).toFixed(1);
    };

    const ReviewSection = ({ reviews, totalReviews, tags }) => {
        const averageRating = calculateAverageRating(reviews);

        setAverageRating(averageRating);
    }

    // ✅ 상품 문의 작성 버튼 클릭 시 실행
    const handleQnaWriteClick = () => {
        if (!memberId) {
            // ❌ 로그인 안 한 경우 → 로그인 유도 모달 열기
            setIsLoginModalOpen(true);
        } else {
            // ✅ 로그인한 경우 → 상품 문의 작성 모달 열기
            setIsQnaModalOpen(true);
        }
    };

    // ✅ Q&A 작성 후 데이터 갱신 함수
    const refreshQnaList = async () => {
        try {
            const response = await fetch(`/api/items/detail?itemId=${item.itemId}`);
            const data = await response.json();
            if (data.qnaArticles !== "noData") {
                setQaList(data.qnaArticles); // ✅ 최신 Q&A 목록 반영
            }
        } catch (error) {
            console.error("Q&A 목록 갱신 실패:", error);
        }
    };

    // 유튜브 URL에서 영상ID 추출 함수
    const getYoutubeId = (url) => {
        if (!url) return null;
        const regExp = /(?:https?:\/\/)?(?:www\.)?youtu(?:\.be\/|be\.com\/(?:watch\?v=|embed\/|v\/|shorts\/|user\/.*?#!v=))([^&?\/\s]{11})/;
        const match = url.match(regExp);
        return match ? match[1] : null;
    };

    // 동영상 모달 열기
    const openVideoModal = (videoUrl) => {
        setSelectedVideo(videoUrl);
    };

    // 동영상 모달 닫기
    const closeVideoModal = () => {
        setSelectedVideo(null);
    };

    return (
        <div className="product-detail-extra">
            {/* 탭 네비게이션 */}
            <div className="tab-container">
                <button
                    className={`tab-button ${activeTab === "detail" ? "active" : ""}`}
                    onClick={() => setActiveTab("detail")}
                >
                    상세정보
                </button>
                <button
                    className={`tab-button ${activeTab === "review" ? "active" : ""}`}
                    onClick={() => setActiveTab("review")}
                >
                    리뷰
                </button>
                <button
                    className={`tab-button ${activeTab === "qa" ? "active" : ""}`}
                    onClick={() => setActiveTab("qa")}
                >
                    Q&A
                </button>
            </div>

            {/* 탭 내용 */}
            <div className="tab-content">
                {activeTab === "detail" && (
                    <div className="detail-content">
                        {/* 동영상 섹션 추가 */}
                        {videoUrl && (
                            <div className="product-video-section">
                                <h3 className="video-title">제품 소개 영상</h3>
                                <div className="video-card" onClick={() => openVideoModal(videoUrl)}>
                                    <div className="video-thumbnail">
                                        <img 
                                            src={`https://img.youtube.com/vi/${getYoutubeId(videoUrl)}/maxresdefault.jpg`}
                                            alt="제품 소개 영상 썸네일" 
                                        />
                                        <div className="play-button">▶</div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* 상세 이미지 */}
                        <div className="detail-images">
                            {image.length > 0 ? (
                                <img
                                    src={image}
                                    alt={`${item.itemName} 상품 상세 이미지`}
                                    className="product-image"
                                />
                            ) : (
                                <div className="empty-image-placeholder">이미지가 없습니다.</div>
                            )}
                        </div>
                    </div>
                )}
                {/* 리뷰 탭 */}
                {activeTab === "review" && (
                    <div className="review-section p-4 bg-white rounded-2xl shadow-md">
                        <div className="flex flex-col items-center mb-4">
                            <h2 className="text-2xl font-bold">리뷰</h2>
                            <div className="flex items-center mt-2">
                                <span className="text-4xl font-bold mr-2">{averageRating}</span>
                                <span className="text-xl">/5</span>
                            </div>
                            <p className="text-gray-500">전체 리뷰수 {reviewList.length} 개</p>
                        </div>

                        <div className="review-list space-y-4">
                            {reviews.map((review) => (
                                <div key={review.articleId} className="review-item p-4 bg-gray-100 rounded-xl shadow-sm">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center">
                                            <span className="font-semibold mr-2">{review.loginId}</span>
                                            <span className="text-gray-500 text-sm">{review.postDate}</span>
                                        </div>
                                        <span className="text-red-500 font-bold">★ {review.rate}</span>
                                    </div>
                                    <p className="text-gray-700 mb-2">{review.content}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {activeTab === "qa" && (
                    <div className="qa-section p-4 bg-white rounded-2xl shadow-md">
                        <div className="qna-page">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-2xl font-bold">Q&A <span className="text-xl font-semibold ml-2"> {qaList.length} 건</span></h2>
                                <button className="write-qna" onClick={handleQnaWriteClick}>
                                    Q&A 작성하기
                                </button>
                            </div>

                            {/* 모달 */}
                            <QnaModal isOpen={isQnaModalOpen} onClose={() => setIsQnaModalOpen(false)} itemId={item.itemId} itemName={item.itemName} onQnaSubmitted={refreshQnaList} />
                            <LoginRequiredModal
                                isOpen={isLoginModalOpen}
                                onClose={() => setIsLoginModalOpen(false)}
                                onConfirm={() => {
                                    // 화면 최상단 이동
                                    window.scrollTo(0, 0);
                                    // 로그인 페이지로 이동
                                    navigate("/login");

                                }} // ✅ 로그인 페이지 이동
                            />
                        </div>
                        <table className="w-full border-collapse border border-gray-200">
                            <thead>
                            <tr className="bg-gray-100">
                                <th className="border p-2">번호</th>
                                <th className="border p-2">답변상태</th>
                                <th className="border p-2">문의내용</th>
                                <th className="border p-2">아이디</th>
                                <th className="border p-2">등록일</th>
                            </tr>
                            </thead>
                            <tbody>
                            {qaList.length > 0 ? qaList.map((qa, index) => (
                                <tr key={qa.articleId} className="border text-center">
                                    <td className="border p-2">{index + 1}</td>
                                    <td className={`border p-2 ${qa.comment ? "text-green-500" : "text-red-500"}`}>
                                        {qa.comment ? "답변완료" : "답변대기"}
                                    </td>
                                    <td className="border p-2 text-left">
                                        {qa.visibility === 'PRIVATE' && !qa.myArticle ? (
                                            "🔒 비밀글 입니다."
                                        ) : (
                                            <>
                                                <strong>Q</strong> {qa.title}
                                                {qa.content && <p>{qa.content}</p>}
                                                {qa.comment && (
                                                    <div className="mt-2 text-gray-700">
                                                        <strong>A</strong> {qa.comment}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td className="border p-2">{qa.loginId}</td>
                                    <td className="border p-2">{qa.postDate}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="text-center p-4 text-gray-500">Q&A가 없습니다.</td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            {/* 동영상 모달 */}
            {selectedVideo && (
                <div className="modal-overlay" onClick={closeVideoModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeVideoModal}>&times;</button>
                        <iframe
                            src={`https://www.youtube.com/embed/${getYoutubeId(selectedVideo)}`}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="youtube-video"
                        ></iframe>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductDetailExtra;
